import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import {
    DaButton,
    DaLabel,
    CrossBoldIcon,
    InternalGrid,
    Message as UIMessage,
    Text,
    OrganizationIcon,
    UploadIcon,
    ButtonGroup,
    Image,
} from '@paygreen/paygreen-ui';
import { Message } from '../../../components';
import regexForm from '../../../utils/regexForm';
import { imageFormats } from '../../../utils/constants';
import {
    FileUploadInput,
    FileUploadContainer,
    FormatedLink,
    ImageContainer,
} from './UploadLogo.styled';

const UploadLogoForm = ({
    t,
    register,
    errors,
    onSubmit,
    children,
    tempFile,
    setTempFile,
    setErrorMessage,
    reset,
    errorMessage,
    setError,
    setBase64,
    base64,
    clearErrors,
}) => {
    const [isDragActive, setIsDragActive] = useState(false);
    const inputImageRef = useRef(null);

    const removeFiles = () => {
        setErrorMessage();
        reset();
        setTempFile();
        setBase64();
    };

    const convertBytesToKB = (bytes) => Math.round(bytes / 1000);

    const simulateInputClick = () => {
        inputImageRef.current && inputImageRef.current.click();
    };

    const addNewTempFile = (e) => {
        e.preventDefault();

        const reader = new FileReader();
        const file = e.target.files[0];

        !!file && file!== tempFile && clearErrors();
        setErrorMessage();

        if (reader !== undefined && file !== undefined) {
            reader.onloadend = () => {
                setTempFile(file);
                setBase64(reader.result);
            };
            reader.readAsDataURL(file);

            if (!imageFormats.includes(file.type)) {
                setError('inputImage', {
                    type: 'string',
                    message: t('form.error.inputImage'),
                });
            } else if (file.size > 500000)
                setError('inputImage', {
                    type: 'string',
                    message: t(`form.error.inputCsvBig`),
                });
        }
    };

    return (
        <>
            <form onSubmit={onSubmit} noValidate encType="multipart/form-data">
                <InternalGrid
                    displayType="grid"
                    hasStaticWidth={true}
                    justifyItems="stretch"
                    alignItems="center"
                >
                    <ButtonGroup align="center" marginBottom="sm">
                        {base64 && !errors['inputImage'] ? (
                            <ImageContainer hasBorder={true}>
                                <Image shadowSize="none">
                                    <img src={base64} alt="Logo" />
                                </Image>
                            </ImageContainer>
                        ) : (
                            <ImageContainer hasBorder={true}>
                                <OrganizationIcon
                                    colorTheme="quaternary"
                                    iconSize="xl"
                                />
                            </ImageContainer>
                        )}
                    </ButtonGroup>

                    <FileUploadContainer
                        isDragActive={isDragActive}
                        onDragOver={() => setIsDragActive(true)}
                        onDragLeave={() => setIsDragActive(false)}
                        onDrop={() => setIsDragActive(false)}
                    >
                        <InternalGrid
                            displayType="grid"
                            hasStaticWidth={true}
                            justifyItems="center"
                            alignItems="center"
                        >
                            <FileUploadInput
                                id="inputImage"
                                name="inputImage"
                                type="file"
                                accept=".jpeg, .png, .jpg, .svg"
                                ref={(e) => {
                                    register(e, {
                                        required: t('form.error.requiredFile'),
                                        pattern: {
                                            value: regexForm.imageFile,
                                            message: t('form.error.inputImage'),
                                        },
                                        validate: (value) =>
                                            value.length &&
                                            value[0].size > 500000
                                                ? t(`form.error.inputCsvBig`)
                                                : null,
                                    });
                                    inputImageRef.current = e;
                                }}
                                onChange={addNewTempFile}
                                src={base64}
                            />

                            <DaLabel htmlFor="inputImage">
                                <Text
                                    textSize="sm"
                                    align="center"
                                    marginBottom="xs"
                                    colorWab="grey30"
                                    className="upload-text"
                                >
                                    {t('charity.see.dragText')}
                                </Text>
                            </DaLabel>

                            <button
                                type="button"
                                onClick={simulateInputClick}
                                style={{
                                    pointerEvents: isDragActive ? 'none' : null,
                                }}
                            >
                                <DaButton
                                    icon={<UploadIcon />}
                                    buttonSize="sm"
                                    buttonStyle="line"
                                    colorTheme="secondary"
                                >
                                    {t('form.button.drop')}
                                </DaButton>
                            </button>

                            <InternalGrid
                                childrenMarginTop={tempFile ? 'md' : 'sm'}
                                childrenMarginLateral="xs"
                                alignItems="center"
                                flexWrap="nowrap"
                            >
                                {tempFile ? (
                                    <>
                                        <Text colorWab="grey60" textSize="sm">
                                            {tempFile.name}
                                            &nbsp; (
                                            {convertBytesToKB(tempFile.size)}
                                            Ko)
                                        </Text>

                                        <CrossBoldIcon
                                            htmlTag="button"
                                            colorPallet="status"
                                            colorStatus="danger"
                                            hasBackground={true}
                                            hasHover={true}
                                            iconSize="xs"
                                            onClick={removeFiles}
                                            style={{ flexShrink: 0 }}
                                        />
                                    </>
                                ) : (
                                    <Text colorWab="grey60" textSize="sm">
                                        {t('charity.see.noFile')}
                                    </Text>
                                )}
                            </InternalGrid>
                        </InternalGrid>
                    </FileUploadContainer>

                    <Text
                        marginBottom="xs"
                        textSize="xs"
                        colorPallet="wab"
                        colorWab="grey30"
                    >
                        {t('charity.see.linkTinyPNG')}

                        <FormatedLink to="https://tinypng.com/">
                            TinyPNG
                        </FormatedLink>

                        {t('charity.see.beforeUpload')}
                    </Text>

                    {errors.inputImage && (
                        <UIMessage
                            arrowBlock="none"
                            colorStatus="warning"
                            hasStaticWidth={true}
                            marginTop="md"
                            marginBottom="sm"
                        >
                            <Text align="center" textSize="xs">
                                {errors.inputImage?.message}
                            </Text>
                        </UIMessage>
                    )}

                    {errorMessage && (
                        <Message
                            message={errorMessage}
                            type="error"
                            paddingLateral="sm"
                            marginTop="md"
                            marginBottom="xs"
                            blockWidth="lg"
                            hasStaticWidth={true}
                        />
                    )}

                    {!errors.inputImage && children}
                </InternalGrid>
            </form>
        </>
    );
};

UploadLogoForm.propTypes = {
    register: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    setError: PropTypes.func.isRequired,
    /**
     * errors passed by react-hook-form for each field (necessary if you want errors management)
     */
    errors: PropTypes.object,
    tempFile: PropTypes.object,
    setTempFile: PropTypes.func,
    errorMessage: PropTypes.string,
    setErrorMessage: PropTypes.func,
};

UploadLogoForm.defaultProps = {
    errors: {},
    tempFile: null,
    errorMessage: null,
};

export default withTranslation('common')(UploadLogoForm);
